import { Injectable } from '@angular/core';
import { 
  CanActivate, 
  CanActivateChild, 
  ActivatedRouteSnapshot, 
  RouterStateSnapshot, 
  Router 
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthorizationService } from '../services/authorization.service';
import { ToastService } from '../services/toast.service';
import { AuthService } from '../services/auth.service';

/**
 * Guard para proteger rutas según permisos del usuario.
 * 
 * Uso en rutas:
 * {
 *   path: 'projects/new',
 *   component: NewProjectComponent,
 *   canActivate: [AuthGuard, PermissionGuard],
 *   data: { permissions: ['PROJECTS_CREATE'] }
 * }
 * 
 * Con múltiples permisos (OR por defecto):
 * data: { permissions: ['PROJECTS_CREATE', 'PROJECTS_UPDATE'] }
 * 
 * Requerir todos los permisos (AND):
 * data: { permissions: ['PROJECTS_CREATE', 'PROJECTS_UPDATE'], requireAll: true }
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate, CanActivateChild {

  constructor(
    private authorizationService: AuthorizationService,
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPermissions(route, state.url);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.checkPermissions(childRoute, state.url);
  }

  private checkPermissions(route: ActivatedRouteSnapshot, url: string): boolean {
    // Primero verificar si está autenticado
    if (!this.authService.isAuthenticated()) {
      this.toastService.showToast('warning', 'Debes iniciar sesión para acceder a esta página');
      this.router.navigate(['/login'], { queryParams: { returnUrl: url } });
      return false;
    }

    // Recargar permisos desde el token por si cambiaron
    this.authorizationService.refreshPermissions();

    // Obtener permisos requeridos de la ruta
    const requiredPermissions = route.data['permissions'] as string[] | undefined;
    const requireAll = route.data['requireAll'] as boolean | undefined;

    // Si no hay permisos requeridos, permitir acceso
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }

    // Verificar permisos
    let hasPermission: boolean;

    if (requireAll) {
      hasPermission = this.authorizationService.hasAllPermissions(requiredPermissions);
    } else {
      hasPermission = this.authorizationService.hasAnyPermission(requiredPermissions);
    }

    if (!hasPermission) {
      this.toastService.showToast(
        'warning', 
        'No tienes permisos para acceder a esta sección'
      );
      
      // Redirigir al dashboard
      this.router.navigate(['/']);
      return false;
    }

    return true;
  }
}

/**
 * Guard específico para rutas que requieren rol de administrador
 */
@Injectable({
  providedIn: 'root'
})
export class AdminGuard implements CanActivate {

  constructor(
    private authorizationService: AuthorizationService,
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    this.authorizationService.refreshPermissions();

    if (!this.authorizationService.isAdmin()) {
      this.toastService.showToast(
        'warning', 
        'Esta sección requiere permisos de administrador'
      );
      this.router.navigate(['/']);
      return false;
    }

    return true;
  }
}

/**
 * Guard específico para rutas que requieren rol de Super Administrador
 */
@Injectable({
  providedIn: 'root'
})
export class SuperAdminGuard implements CanActivate {

  constructor(
    private authorizationService: AuthorizationService,
    private authService: AuthService,
    private router: Router,
    private toastService: ToastService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }

    this.authorizationService.refreshPermissions();

    if (!this.authorizationService.isSuperAdmin()) {
      this.toastService.showToast(
        'warning', 
        'Esta sección requiere permisos de Super Administrador'
      );
      this.router.navigate(['/']);
      return false;
    }

    return true;
  }
}

